import {
  Card,
  Chart,
  ChartVizType,
  PivotTable,
  StatFigure,
} from '@employ-engage/common-components';
import { ChartResult } from '@employ-engage/data';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  availableMeasures,
  ChartableField,
  MEASURE_LABELS,
  MeasureKind,
} from './chart-spec-builder';

/** A card shows a plot or the same cross-tab as a table. */
export type CardViz = ChartVizType | 'table';

export interface ChartCardProps {
  field: ChartableField;
  result?: ChartResult | null;
  loading?: boolean;
  error?: string | null;
  viz: CardViz;
  onVizChange: (viz: CardViz) => void;
  asPercent: boolean;
  onAsPercentChange: (asPercent: boolean) => void;
  /** Toggles: pins when this card is not pinned, unpins when it is. */
  onPin?: () => void;
  /** Whether a chart with exactly this configuration is on the dashboard. */
  pinned?: boolean;
  /**
   * Pinned charts of this question drawn some other way.
   *
   * Without this, changing a pinned bar chart to a pie empties the pin icon
   * and looks like the pin was lost — when in fact the bar is still there and
   * the pie is simply a different chart.
   */
  otherPinnedViews?: number;
  /** A pin or an unpin is in flight for this card. */
  pinning?: boolean;
  onScore?: () => void;
  scoringOpen?: boolean;
  measure: MeasureKind;
  onMeasureChange: (measure: MeasureKind) => void;
  children?: React.ReactNode;
  className?: string;
}

const VIZ_OPTIONS: { value: CardViz; icon: string; label: string }[] = [
  { value: 'horizontalBar', icon: 'fa-chart-bar', label: 'Bars' },
  { value: 'bar', icon: 'fa-chart-column', label: 'Columns' },
  { value: 'pie', icon: 'fa-chart-pie', label: 'Pie' },
  { value: 'donut', icon: 'fa-circle-notch', label: 'Donut' },
  { value: 'table', icon: 'fa-table', label: 'Table' },
];

/**
 * One question's results, with the controls that matter most sitting on the
 * card rather than behind a dialog.
 */
export function ChartCard({
  field,
  result,
  loading,
  error,
  viz,
  onVizChange,
  asPercent,
  onAsPercentChange,
  onPin,
  pinned,
  otherPinnedViews = 0,
  pinning,
  onScore,
  scoringOpen,
  measure,
  onMeasureChange,
  children,
  className,
}: ChartCardProps) {
  const { t } = useTranslation(['common']);

  const measures = availableMeasures(field);
  // With no breakdown a scored measure is one number, and a number should look
  // like a number rather than a chart with a single bar.
  const isSingleFigure =
    measure !== 'distribution' && result?.cells?.length === 1;
  const reworded = field.wordings.length > 1;
  const multiSelect = field.questionType === 'select many';
  const suppressed = result?.meta?.suppressedAll;

  return (
    <Card className={classNames('col-span-12 lg:col-span-6', className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-light-primaryText dark:text-dark-primaryText">
            {field.label}
          </h3>
          {reworded && (
            // A chart spanning a reword shows one continuous series. Saying so
            // is the difference between a helpful merge and a silent one.
            <p className="mt-1 text-xs text-light-secondaryText dark:text-dark-secondaryText">
              {t('Previously: "{{text}}"', {
                text: field.wordings[field.wordings.length - 2]?.text,
              })}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {onScore && (
            <button
              type="button"
              onClick={onScore}
              title={t('Score these answers')}
              aria-label={t('Score these answers')}
              aria-pressed={scoringOpen}
              className={classNames(
                'rounded px-2 py-1 text-xs',
                scoringOpen
                  ? 'bg-light-primary text-white'
                  : 'text-light-secondaryText hover:bg-light-alternate'
              )}
            >
              <i className="fa-solid fa-ruler" aria-hidden />
            </button>
          )}
          {onPin && (
            // One button, both directions. It used to disable itself once
            // pinned, which left no way back and no way to tell a chart that
            // was pinned in this session from one pinned last week — the state
            // never survived a reload.
            <button
              type="button"
              onClick={onPin}
              disabled={pinning}
              aria-pressed={!!pinned}
              title={
                pinning
                  ? t('Working…')
                  : pinned
                  ? t(
                      'This exact chart is on your dashboard. Click to remove it.'
                    )
                  : otherPinnedViews > 0
                  ? t(
                      'Pin this version too — {{count}} other view of this question is already pinned.',
                      { count: otherPinnedViews }
                    )
                  : t('Pin to dashboard')
              }
              aria-label={
                pinned ? t('Remove from dashboard') : t('Pin to dashboard')
              }
              className={classNames(
                'rounded px-2 py-1 text-xs transition-colors',
                pinning && 'opacity-50',
                pinned
                  ? 'bg-light-primary/10 text-light-primary hover:bg-light-primary/20'
                  : 'text-light-secondaryText hover:bg-light-alternate'
              )}
            >
              <i
                className={classNames(
                  pinned ? 'fa-solid' : 'fa-regular',
                  'fa-thumbtack'
                )}
                aria-hidden
              />
              {!pinned && otherPinnedViews > 0 && (
                // Quiet on purpose: this card is not pinned and the hollow pin
                // says so. The number is only there to explain why a question
                // you know you pinned looks unpinned — you pinned it as
                // something else.
                <span className="ml-1 text-[10px] font-medium text-light-primary">
                  {otherPinnedViews}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => onAsPercentChange(!asPercent)}
            className={classNames(
              'rounded px-2 py-1 text-xs font-medium',
              asPercent
                ? 'bg-light-primary text-white'
                : 'text-light-secondaryText hover:bg-light-alternate'
            )}
            aria-pressed={asPercent}
          >
            %
          </button>
          {VIZ_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              title={t(option.label)}
              aria-label={t(option.label)}
              aria-pressed={viz === option.value}
              onClick={() => onVizChange(option.value)}
              className={classNames(
                'rounded px-2 py-1 text-xs',
                viz === option.value
                  ? 'bg-light-primary text-white'
                  : 'text-light-secondaryText hover:bg-light-alternate'
              )}
            >
              <i className={classNames('fa-solid', option.icon)} aria-hidden />
            </button>
          ))}
        </div>
      </div>

      {measures.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {measures.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onMeasureChange(option)}
              aria-pressed={measure === option}
              className={classNames(
                'rounded-full px-3 py-1 text-xs',
                measure === option
                  ? 'bg-light-primary text-white'
                  : 'bg-light-alternate text-light-secondaryText'
              )}
            >
              {t(MEASURE_LABELS[option])}
            </button>
          ))}
        </div>
      )}

      <div className="mt-4 h-64 overflow-auto">
        {isSingleFigure ? (
          <StatFigure result={result} measure={measure as never} />
        ) : viz === 'table' ? (
          // The same cross-tab the plot is drawn from, so the two can never
          // disagree about a number.
          <PivotTable
            result={result}
            display={asPercent ? 'both' : 'count'}
            heatmap
          />
        ) : (
          <Chart
            result={result}
            type={viz}
            loading={loading}
            error={error}
            valueFormat={asPercent ? 'percent0' : 'integer'}
          />
        )}
      </div>

      <div className="mt-3 min-h-[1.25rem] text-xs text-light-secondaryText dark:text-dark-secondaryText">
        {!suppressed && result && (
          <span>
            {t('{{count}} responses', {
              count: result.meta.respondentsInScope,
            })}
          </span>
        )}
        {!suppressed && multiSelect && (
          // Without this the percentages look broken rather than correct.
          <span className="ml-2">
            {t('Respondents could choose more than one answer.')}
          </span>
        )}
        {!suppressed && !!result?.meta?.naWithSelections && (
          // A survey-design smell worth showing rather than quietly resolving.
          <span className="ml-2">
            {t(
              '{{count}} also chose an answer meaning it did not apply; they are still counted.',
              { count: result.meta.naWithSelections }
            )}
          </span>
        )}
      </div>

      {children}
    </Card>
  );
}

export default ChartCard;
